/**
 * Trigger identifiers persisted by custom workflow notifications.
 *
 * These values must stay aligned with the database's
 * `notification_trigger_enum`.
 */
export const NOTIFICATION_TRIGGERS = [
  'row_edited',
  'advanced',
  'reverted',
  'removed',
  'restored',
  'row_comments_changed',
  'option_state_changed',
] as const

/**
 * A trigger identifier stored by a custom workflow notification template.
 */
export type NotificationTriggerType = (typeof NOTIFICATION_TRIGGERS)[number]

/**
 * Field-change filters stored in notification trigger settings.
 */
export type NotificationFieldChangeSettingsType = {
  enabled: boolean
  /**
   * The value of at least one of the specified fields changed.
   */
  anyFieldIdsChanged: string[]
  /**
   * The value of every specified field changed.
   */
  allFieldIdsChanged: string[]
  /**
   * None of the values of any specified fields changed.
   *
   * @example
   * const none = !changed(A) && !changed(B) && !changed(C)
   */
  noFieldIdsChanged: string[]
}

/**
 * Settings for notifications that fire after a saved row edit.
 */
export type RowEditedTriggerSettingsType =
  NotificationFieldChangeSettingsType & {
    type: 'row_edited'
  }

type DraftStatus = { type: 'draft' }
type ClosedStatus = { type: 'closed' }
type StatusTransition = { type: 'status'; statusId: string }
type StatusReference = DraftStatus | ClosedStatus | StatusTransition

/**
 * Settings for notifications that fire from an advanced workflow action.
 */
export type AdvancedTriggerSettingsType = {
  type: 'advanced'
  useAdvancedSettings: null | NotificationFieldChangeSettingsType
  /**
   * Empty matches any valid source, including creation with no previous record.
   * A populated array matches any listed reference; `[{ type: 'create' }]`
   * selects creation alone. Draft is a separate lifecycle state.
   */
  from: ({ type: 'create' } | DraftStatus | StatusTransition)[]
  /**
   * Empty matches any valid destination; a populated array matches any listed
   * reference. Neither side bypasses field-change filters or conditions.
   */
  to: (ClosedStatus | StatusTransition)[]
}

/**
 * Settings for notifications that fire from a reverted workflow action.
 */
export type RevertedTriggerSettingsType = {
  type: 'reverted'
  useAdvancedSettings: null | NotificationFieldChangeSettingsType
  /**
   * Empty matches any valid reverted source; a populated array matches any
   * listed reference. Reverted transitions never include creation.
   */
  from: (ClosedStatus | StatusTransition)[]
  /**
   * Empty matches any valid reverted destination; a populated array matches any
   * listed reference. Neither side bypasses field-change filters or conditions.
   */
  to: (DraftStatus | StatusTransition)[]
}

/**
 * Settings for notifications that fire when an approvals option state changes.
 */
export type OptionStateChangedTriggerSettingsType = {
  type: 'option_state_changed'
  fieldId: string
}

/**
 * A persisted non-null `notifications.triggerSettings` value.
 */
export type NotificationTriggerSettingsType =
  | RowEditedTriggerSettingsType
  | AdvancedTriggerSettingsType
  | RevertedTriggerSettingsType
  | OptionStateChangedTriggerSettingsType

/**
 * Requires a select-like field to include at least one configured option.
 */
export type IncludesOptionsConditionType = {
  type: 'includes_options'
  fieldId: string
  optionIds: string[]
}

/**
 * Requires every option state on an approvals-style field to be filled.
 */
export type AllOptionStatesFilledConditionType = {
  type: 'all_option_states_filled'
  fieldId: string
}

/**
 * Requires an approvals-style field to have an empty option state.
 */
export type AnyOptionStateEmptyConditionType = {
  type: 'any_option_state_empty'
  fieldId: string
}

/**
 * Requires the record's lifecycle status to be one of the configured statuses.
 *
 * Draft and Closed are system statuses without custom status IDs.
 *
 * @example
 * const condition: StatusIncludesConditionType = {
 *   type: 'status_includes',
 *   statuses: [
 *     { type: 'draft' },
 *     { type: 'status', statusId: '55555555-5555-4555-8555-555555555555' },
 *     { type: 'closed' },
 *   ],
 * }
 */
export type StatusIncludesConditionType = {
  type: 'status_includes'
  statuses: StatusReference[]
}

/**
 * A condition that must pass before a custom workflow notification is sent.
 */
export type NotificationConditionType =
  | IncludesOptionsConditionType
  | AllOptionStatesFilledConditionType
  | AnyOptionStateEmptyConditionType
  | StatusIncludesConditionType

/**
 * Condition identifiers supported by custom workflow notifications.
 */
export const NOTIFICATION_CONDITION_TYPES = [
  'all_option_states_filled',
  'any_option_state_empty',
  'includes_options',
  'status_includes',
] as const satisfies readonly NotificationConditionType['type'][]
